/**
 * Shaping a planner route for the mission API.
 *
 * Two callers save a route — the standalone planner page and the workspace
 * dialog — and each now saves two of them. Four copies of the same field
 * mapping is four chances for the alternate to be stored in a shape the primary
 * is not.
 */
import { MissionAlternateRoute, MissionRouteStats } from '../../../services/missionApi';
import { RouteResult } from '../types';

export const DEFAULT_PRIMARY_NAME = 'Primary';
export const DEFAULT_ALTERNATE_NAME = 'Alternate';

export function namedRouteLabel(value: string | null | undefined, fallback: string): string {
  const trimmed = (value || '').trim();
  return trimmed || fallback;
}

export function activeRouteLabel(mission: {
  active_route_id?: string | null;
  saved_routes?: Array<{ id: string; label?: string | null }> | null;
  route_options?: Record<string, unknown> | null;
  alt_route?: { label?: string | null } | null;
}): string {
  const liveId = (mission.active_route_id || '').trim();
  const named = (mission.saved_routes || []).find((route) => route.id === liveId);
  if (named?.label?.trim()) return named.label.trim();
  return missionRouteNames(mission).primary;
}

export function missionRouteNames(mission: {
  route_options?: Record<string, unknown> | null;
  alt_route?: { label?: string | null } | null;
}): { primary: string; fallback: string | null } {
  const raw = mission.route_options?.route_name;
  const primary = typeof raw === 'string' ? namedRouteLabel(raw, DEFAULT_PRIMARY_NAME) : DEFAULT_PRIMARY_NAME;
  const fallback = mission.alt_route
    ? namedRouteLabel(mission.alt_route.label, DEFAULT_ALTERNATE_NAME)
    : null;
  return { primary, fallback };
}

export function withRouteName(
  options: Record<string, unknown>,
  name: string | null | undefined,
): Record<string, unknown> {
  return { ...options, route_name: namedRouteLabel(name, DEFAULT_PRIMARY_NAME) };
}

/** Streets Mapbox named on this line, used as a suggested fallback name. */
export function routeStreetSummary(route: RouteResult | null | undefined): string {
  if (!route) return '';
  return route.legs.map((leg) => leg.summary).filter(Boolean).join(', ');
}

/**
 * Compact fingerprint so a stored fallback can be matched after Mapbox
 * returns the same set of lines. Full coordinates would be a huge key and
 * the ends-plus-midpoint is enough to tell two Mapbox alternatives apart.
 */
export function geometryKey(geometry: GeoJSON.LineString | null | undefined): string {
  const coords = geometry?.coordinates;
  if (!coords || coords.length < 2) return '';
  const pick = (index: number) => {
    const point = coords[index];
    return `${Number(point[0]).toFixed(5)},${Number(point[1]).toFixed(5)}`;
  };
  return `${coords.length}:${pick(0)}:${pick(Math.floor(coords.length / 2))}:${pick(coords.length - 1)}`;
}

export function routeStats(route: RouteResult | null, profile: string): MissionRouteStats {
  if (!route) return {};
  return {
    distance: route.distance,
    duration: route.duration,
    traffic_delay: route.durationTypical != null
      ? route.duration - route.durationTypical
      : undefined,
    profile,
  };
}

/**
 * The alternate as the API wants it.
 *
 * Returns null when no fallback was kept, which the caller pairs with
 * `clear_alternate` — saving a route with no alternate selected has to remove a
 * previously stored one, or an operator who deliberately dropped the fallback
 * would still be offered a switch to it mid-run.
 */
export function alternatePayload(
  route: RouteResult | null,
  profile: string,
  options: Record<string, unknown>,
  label?: string | null,
  waypoints?: Array<{ label?: string | null; latitude: number; longitude: number; notes?: string | null }>,
): Omit<MissionAlternateRoute, 'signature' | 'saved_at'> | null {
  if (!route?.geometry) return null;
  return {
    geometry: route.geometry,
    stats: routeStats(route, profile),
    profile,
    options,
    label: namedRouteLabel(label, DEFAULT_ALTERNATE_NAME),
    // Stored with the alternate so a mid-run switch can rejoin onto *its*
    // stops. The planner's fallback shares the primary's stops today, but the
    // stored plan should say so itself rather than leave the server to guess.
    ...(waypoints && waypoints.length ? { waypoints } : {}),
  };
}
